/**
 * Text Component
 */

import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import {
  formatComponentAlignmentFormat,
  formatColorFormat,
  formatCornerRadiusFormat,
  formatFromVariableText,
  formatSpacingFormat,
  formatTextAlignmentFormat,
  formatTypographyFormat,
  formatWidthFormat,
} from '../../format-utils'
import {
  createResponsiveValue,
  deepMerge,
  parseAlignmentFormat,
  parseColorFormat,
  parseCornerRadiusFormat,
  parseSpacingFormat,
  parseToVariableText,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import type {
  ColorAdaptiveValue,
  ColorVariant,
  ComponentAlignment,
  GradientDirection,
  OverflowSettings,
  ResponsiveValue,
  SpacingInputValue,
  TextAlignment,
  TypographyValue,
  VariableTextValue,
} from '../../types'
import textTemplate from './text.template.json'

export interface ITextComponentSettings {
  'text-data-value': VariableTextValue
  'text-data-url': any
  'text-design-color': ColorAdaptiveValue<string>
  'text-design-color-solid': ColorAdaptiveValue<ColorVariant>
  'text-design-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'text-design-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'text-design-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'text-design-width': OverflowSettings<ResponsiveValue<string[] | number[]>>
  'text-design-typography': ResponsiveValue<TypographyValue>
  'text-design-padding': ResponsiveValue<SpacingInputValue>
  'text-design-margin': OverflowSettings<ResponsiveValue<SpacingInputValue>>
  'text-design-border': string[]
  'text-design-border-width': string[] | number[]
  'text-design-border-color': ColorVariant
  'text-design-border-style': string
  'text-design-corner-radius': string
  'text-design-comp-h-align': ResponsiveValue<ComponentAlignment>
  'text-design-h-align': ResponsiveValue<TextAlignment>
  'text-design-hover-effect'?: string // Hover effect: 'none' | 'underline' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'
  'text-action-click': BuilderActions[]
}

export interface ITextComponent extends IBuilderComponent {
  componentId: 'text'
  groupId: 'text'
  settings: ITextComponentSettings
}

export interface AiTextInput {
  content: string[] // Format: ["text:Hello World"] or ["text:The best product is ", "sc:products:name:12"]
  typography?: string // Format: "body-medium" or "body-medium ta:body-large de:body-extra-large"
  color?: string // Format: "solid:primary-40" or "gradient:primary-40|primary-80:to-right" or "adaptive"
  align?: string // Format: "t:center" or "t:center ta:t:left de:t:right"
  padding?: string // Format: "p:1rem" or "pt:20px pb:20px" or "p:1rem ta:p:2rem"
  margin?: string // Format: "m:2rem" or "mb:16px" or "m:2rem ta:m:3rem"
  radius?: string // Format: "medium" or "small ta:medium de:large"
  width?: string // Format: "w:100%" or "w:300px" or "w:100% de:w:800px"
  hoverEffect?: string // Format: "bounce" or "underline" | 'none' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'
  clickAction?: BuilderActions[]
}

const TEXT_DEFAULT_TEMPLATE = textTemplate as unknown as ITextComponentSettings

export class TextComp {
  getTemplate(): ITextComponentSettings {
    return TEXT_DEFAULT_TEMPLATE
  }

  /**
   * format text component settings to AI text input
   */
  format(settings: ITextComponentSettings): AiTextInput {
    const template = this.getTemplate()
    const result: AiTextInput = {
      content: formatFromVariableText(settings['text-data-value']),
    }

    // Typography
    const typography = formatTypographyFormat(settings['text-design-typography'])
    if (typography) {
      result.typography = typography
    }

    // Color
    const color = formatColorFormat({
      colorType: settings['text-design-color'] as any,
      colorSolid: settings['text-design-color-solid'],
      colorStartGradient: settings['text-design-color-start-gradient'],
      colorEndGradient: settings['text-design-color-end-gradient'],
      colorGradientDirection: settings['text-design-color-gradient-direction'],
    })
    if (color) {
      result.color = color
    }

    // Alignment - combine component alignment and text alignment
    const compAlign = formatComponentAlignmentFormat(settings['text-design-comp-h-align'])
    const textAlign = formatTextAlignmentFormat(settings['text-design-h-align'])
    const alignParts: string[] = []
    if (compAlign) {
      alignParts.push(compAlign)
    }
    if (textAlign) {
      alignParts.push(textAlign)
    }
    if (alignParts.length > 0) {
      result.align = alignParts.join(' ')
    }

    // Padding
    const padding = formatSpacingFormat(settings['text-design-padding'])
    if (padding) {
      result.padding = padding
    }

    // Margin - extract from OverflowSettings
    if (settings['text-design-margin']) {
      const margin = formatSpacingFormat(settings['text-design-margin'] as any)
      if (margin) {
        result.margin = margin
      }
    }

    // Corner Radius
    const cornerRadius = formatCornerRadiusFormat(settings['text-design-corner-radius'])
    if (cornerRadius && cornerRadius !== template['text-design-corner-radius']) {
      result.radius = cornerRadius
    }

    // Width - extract from OverflowSettings
    if (settings['text-design-width']) {
      const width = formatWidthFormat(settings['text-design-width'] as any)
      if (width) {
        result.width = width
      }
    }

    // Hover Effect
    if (settings['text-design-hover-effect']) {
      const hoverEffect = settings['text-design-hover-effect']
      if (hoverEffect && hoverEffect !== 'none') {
        result.hoverEffect = hoverEffect
      }
    }

    // Actions (copied as-is)
    if (settings['text-action-click']) {
      result.clickAction = settings['text-action-click']
    }

    return result
  }

  /**
   * parse AI text input to text component settings
   */
  parse(input: AiTextInput): ITextComponentSettings {
    const result = deepMerge(TEXT_DEFAULT_TEMPLATE, {})

    if (input.content) {
      result['text-data-value'] = parseToVariableText(input.content)
    }

    if (input.typography) {
      result['text-design-typography'] = parseTypographyFormat(input.typography)
    }

    if (input.color) {
      const colorParsed = parseColorFormat(input.color)
      result['text-design-color'] = colorParsed.colorType
      result['text-design-color-solid'] = colorParsed.colorSolid
      result['text-design-color-start-gradient'] = colorParsed.colorStartGradient
      result['text-design-color-end-gradient'] = colorParsed.colorEndGradient
      result['text-design-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    if (input.align) {
      const alignmentParsed = parseAlignmentFormat(input.align)
      if (alignmentParsed.textAlign) {
        result['text-design-h-align'] = alignmentParsed.textAlign
      }
      if (alignmentParsed.hAlign) {
        result['text-design-comp-h-align'] = alignmentParsed.hAlign
      }
    }

    if (input.padding) {
      result['text-design-padding'] = parseSpacingFormat(input.padding)
    }

    if (input.margin) {
      const marginParsed = parseSpacingFormat(input.margin)
      const defaultOverflowSpacing = {
        top: { value: 5, unit: 'px' as const },
        right: { value: 0, unit: 'px' as const },
        bottom: { value: 5, unit: 'px' as const },
        left: { value: 0, unit: 'px' as const },
      }
      result['text-design-margin'] = {
        ...marginParsed,
        overflowSetting: createResponsiveValue(defaultOverflowSpacing),
      }
    }

    if (input.radius) {
      const radiusParsed = parseCornerRadiusFormat(input.radius)
      // Corner radius is stored as a single string, not responsive
      // Use the phone value as default, or first breakpoint value
      result['text-design-corner-radius'] = radiusParsed.phone
    }

    if (input.width) {
      const widthParsed = parseWidthFormat(input.width)
      result['text-design-width'] = {
        ...widthParsed,
        overflowSetting: createResponsiveValue(['100%']),
      }
    }

    // Hover Effect
    if (input.hoverEffect) {
      result['text-design-hover-effect'] = input.hoverEffect
    }

    if (input.clickAction) {
      result['text-action-click'] = input.clickAction
    }

    return result
  }
}
